import { Component, inject, OnInit, OnDestroy, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PasswordModule } from 'primeng/password';
import { PasswordChecklist } from '../../../shared/components/password-checklist/password-checklist';
import { passwordPolicyValidator } from '../../../core/auth/password-policy';
import { AppButton } from '../../../shared/components/app-button/app-button';
import { AuthFooter } from '../../../shared/components/auth-footer/auth-footer';
import { AuthService } from '../../../core/services/auth.service';
import { SupabaseService } from '../../../core/services/supabase.service';
import { toast } from '../../../shared/utils/toast';

/**
 * How long to wait for the Supabase client to turn a recovery token in the URL
 * into a session. Only used when we can see a token was actually delivered —
 * this used to be a blind 2s applied to every case, which reported perfectly
 * good links as expired on a cold client or a slow connection.
 */
const RECOVERY_SESSION_TIMEOUT_MS = 15000;

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PasswordModule,
    AppButton,
    TranslateModule,
    RouterModule,
    AuthFooter,
    PasswordChecklist
  ],
  templateUrl: './reset-password.html',
})
export class ResetPassword implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private supabaseService = inject(SupabaseService);
  readonly router = inject(Router);

  isLoading = false;
  isCheckingSession = true;
  hasValidSession = false; // true only when a recovery session is confirmed
  /** Which message the invalid state shows: a burnt/expired token vs no token at all. */
  invalidReason: 'expired' | 'missing' | 'verify_failed' = 'expired';

  /**
   * Set when the email used the token_hash flow. The token is deliberately NOT
   * redeemed on load — only in onSubmit — so that mail scanners which fetch the
   * link (Microsoft Safe Links) cannot consume it before the user arrives.
   */
  private tokenHash: string | null = null;
  private otpType: 'recovery' | 'invite' = 'recovery';

  /**
   * Captured in a field initializer, which runs before ngOnInit: the client has
   * detectSessionInUrl enabled and SupabaseService is providedIn:'root', so it
   * may consume and strip the hash before this component gets a chance to look.
   * Best-effort only — getSession() below remains the primary signal.
   */
  private readonly urlParams = ResetPassword.readAuthParams();

  private authSubscription: { unsubscribe: () => void } | null = null;
  private timeoutHandle: ReturnType<typeof setTimeout> | null = null;

  /** GoTrue returns tokens in the hash and errors in the hash or the query string. */
  private static readAuthParams(): URLSearchParams {
    const merged = new URLSearchParams();
    if (typeof window === 'undefined') return merged;
    for (const source of [window.location.hash.replace(/^#/, ''), window.location.search.replace(/^\?/, '')]) {
      if (!source) continue;
      new URLSearchParams(source).forEach((value, key) => {
        if (!merged.has(key)) merged.set(key, value);
      });
    }
    return merged;
  }

  /**
   * Remove the consumed credential from the address bar / history without a
   * navigation. Deletes only the params we own, so a PKCE `code` or anything
   * else in the query survives, as does the hash.
   */
  private static scrubTokenFromUrl(): void {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('token_hash')) return;
      url.searchParams.delete('token_hash');
      url.searchParams.delete('type');
      window.history.replaceState(window.history.state, '', url.toString());
    } catch {
      /* non-fatal: the token is already held in memory */
    }
  }

  resetForm: FormGroup = this.fb.group({
    password: ['', [Validators.required, passwordPolicyValidator]],
    confirmPassword: ['', [Validators.required]]
  }, { validators: this.passwordMatchValidator });

  passwordMatchValidator(g: FormGroup) {
    return g.get('password')?.value === g.get('confirmPassword')?.value
      ? null : { 'mismatch': true };
  }

  async ngOnInit() {
    this.ngZone.run(async () => {
      try {
        // 1. GoTrue rejected the link outright — it says so in the URL, so there
        //    is nothing to wait for. error_code distinguishes a burnt/expired
        //    token from a malformed one.
        const errorCode = this.urlParams.get('error_code') || this.urlParams.get('error');
        if (errorCode) {
          console.warn('[ResetPassword] Auth returned an error for this link:', {
            error: this.urlParams.get('error'),
            error_code: this.urlParams.get('error_code'),
            error_description: this.urlParams.get('error_description'),
          });
          this.fail(errorCode === 'otp_expired' || errorCode === 'access_denied' ? 'expired' : 'missing');
          return;
        }

        // 2. token_hash flow — the link did NOT verify anything by being fetched.
        //    Show the form straight away and spend the token only when the user
        //    presses submit (see onSubmit). This is what defeats Outlook/Microsoft
        //    Safe Links: its scanner fetches this page, which costs nothing,
        //    instead of a /auth/v1/verify URL that would burn the one-time token
        //    before the human ever clicks.
        this.tokenHash = this.urlParams.get('token_hash');
        if (this.tokenHash) {
          const type = this.urlParams.get('type');
          this.otpType = type === 'invite' ? 'invite' : 'recovery';
          console.log(`[ResetPassword] token_hash link (${this.otpType}) — deferring verification to submit.`);
          // The token is a bearer credential and, unlike the old #fragment form,
          // a query string is visible in the address bar, kept in history, and
          // sent to the server. We already hold it in memory, so strip it from
          // the visible URL immediately. Only our own two params are removed —
          // the hash is left untouched so the legacy flow still works.
          ResetPassword.scrubTokenFromUrl();
          this.succeed();
          return;
        }

        // 3. Legacy implicit flow: the client already established the session
        //    (it usually wins the race, because it parses the URL at app boot).
        const { data } = await this.supabaseService.client.auth.getSession();
        if (data.session) {
          console.log('[ResetPassword] Valid session found:', data.session.user.email);
          this.succeed();
          return;
        }

        // 4. No session yet. Only wait if a token was actually delivered —
        //    otherwise someone opened /reset-password directly and there is
        //    nothing in flight to wait for.
        if (!this.urlParams.has('access_token')) {
          console.warn('[ResetPassword] No recovery token in URL — not a reset link.');
          this.fail('missing');
          return;
        }

        // Accept a session from ANY event that carries one: depending on the
        // gotrue-js version and timing, a recovery link can arrive as SIGNED_IN
        // rather than PASSWORD_RECOVERY, which the previous code ignored.
        const { data: sub } = this.supabaseService.client.auth.onAuthStateChange(
          (event: string, session: any) => {
            if (!session || this.hasValidSession) return;
            this.ngZone.run(() => {
              console.log(`[ResetPassword] Recovery session received via "${event}"`);
              this.succeed();
            });
          }
        );
        this.authSubscription = sub?.subscription ?? null;

        this.timeoutHandle = setTimeout(() => {
          this.ngZone.run(() => {
            if (!this.hasValidSession) {
              console.warn(
                `[ResetPassword] Token present but no session after ${RECOVERY_SESSION_TIMEOUT_MS}ms.`
              );
              this.fail('expired');
            }
          });
        }, RECOVERY_SESSION_TIMEOUT_MS);
      } catch (err) {
        console.error('[ResetPassword] Error checking session:', err);
        this.fail('expired');
      }
    });
  }

  ngOnDestroy() {
    this.clearTimer();
    this.authSubscription?.unsubscribe();
    this.authSubscription = null;
  }

  private succeed() {
    this.clearTimer();
    this.hasValidSession = true;
    this.isCheckingSession = false;
    this.cdr.detectChanges();
  }

  private fail(reason: 'expired' | 'missing' | 'verify_failed') {
    this.clearTimer();
    this.invalidReason = reason;
    this.hasValidSession = false;
    this.isCheckingSession = false;
    this.cdr.detectChanges();
  }

  private clearTimer() {
    if (this.timeoutHandle !== null) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
  }

  async onSubmit() {
    if (this.resetForm.valid) {
      this.isLoading = true;
      const { password } = this.resetForm.value;
      try {
        // token_hash flow: redeem the one-time token now, on an explicit user
        // gesture, rather than when the link was fetched. A dead token surfaces
        // here for the first time, so report it as an expired link rather than
        // as a generic password-update failure.
        if (this.tokenHash) {
          const { error: verifyError } = await this.supabaseService.client.auth.verifyOtp({
            token_hash: this.tokenHash,
            type: this.otpType,
          });
          if (verifyError) {
            console.warn('[ResetPassword] verifyOtp failed:', verifyError.message);
            this.isLoading = false;
            this.fail('verify_failed');
            return;
          }
          this.tokenHash = null; // single use — don't retry it on a second submit
        }

        const { error } = await this.authService.updatePassword(password);
        if (error) {
          toast.error(this.translate.instant('error'), {
            description: error.message
          });
        } else {
          toast.success(this.translate.instant('reset_password_updated_title'), {
            description: this.translate.instant('reset_password_updated_body')
          });
          // Sign out the temporary recovery session, then go to login
          await this.authService.signOut();
          this.router.navigate(['/login']);
        }
      } catch (err: any) {
        toast.error(this.translate.instant('error'), {
          description: err.message || this.translate.instant('reset_password_update_failed')
        });
      } finally {
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    } else {
      this.resetForm.markAllAsTouched();
    }
  }
}
